/* eslint-disable max-lines, react/no-array-index-key, no-self-compare,  complexity */

import { Alert, Button, Col, Form as AntForm, Row, Typography } from 'antd';
import i18next, { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { BackButton } from 'src/components/BackButton/BackButton';
import { BasePage } from 'src/components/BasePage';
import { Card } from 'src/components/Card/Card';
import { Form } from 'src/components/Form/Form';
import { HiddenField } from 'src/components/Form/HiddenField';
import { ValidationRules } from 'src/components/Form/ValidationRules';
import { EntityLinks } from 'src/core/router/EntityLinks';
import { Router } from 'src/core/router/Router';
import { TaskStore } from 'src/stores/TaskStore/TaskStore';
import { ConstraintViolationError } from 'src/utils/ErrorDataHelper';
import { FormHelper } from 'src/utils/FormHelper';
import styled from 'styled-components';

enum OfferType {
    PREAPPROVED = 'PREAPPROVED',
    HIGHER = 'HIGHER',
}

type Offer = {
    offerType: OfferType;
    offerName: string;
    offerCardEnglish: string;
    offerCardArabic: string;
    partnerName: string;
    partnerSystemLink: string;
    partnerId: string;
};

type TaskVariables = {
    suggestedOffers: Offer[];
};

type FormValues = {
    chosenOffer: Offer;
    cancel: boolean;
};

export const ApplicationsCreateChooseOfferPage = observer(() => {
    const { taskId } = useParams<{ taskId: string }>();
    const [form] = AntForm.useForm<FormValues>();

    const store = TaskStore.getInstance(taskId);

    const fields = FormHelper.getFieldDataItems(
        store.submitTaskFormLoader.errorData as ConstraintViolationError,
    );

    const handleSubmit = async (values: any) => {
        const [nextTask] = (await store.submitTask(values)) ?? [];

        if (nextTask) {
            const nextTaskStore = TaskStore.getInstance(nextTask.id);
            await nextTaskStore.loadTaskVariables();
            if (!nextTaskStore.taskVariablesLoader.hasError) {
                const applicationId =
                    nextTaskStore.taskVariables?.loanApplication?.id;
                if (applicationId) {
                    const link = EntityLinks.loanApplications.itemDetails?.(
                        String(applicationId),
                    );
                    link && Router.replace(link);

                    return;
                }
            }
        }

        if (!store.submitTaskFormLoader.hasError) {
            const link = EntityLinks.loanApplications.list?.();
            link && Router.replace(link);
        }
    };

    const handleChooseOffer = (offer: Offer) => {
        form.setFieldsValue({
            chosenOffer: offer,
            cancel: false,
        });
        form.submit();
    };

    useEffect(() => {
        store.loadTaskAndVariables();

        return () => {
            store.reset();
        };
    }, [taskId]);

    const variables = store.taskVariables as TaskVariables | undefined;
    const hasSuggestedOffers = variables?.suggestedOffers?.length ?? 0 > 0;
    const instantMurabahaOffers =
        variables?.suggestedOffers?.filter(
            (offer) => offer.offerType === OfferType.PREAPPROVED,
        ) ?? ([] as Offer[]);
    const murabahaOffers =
        variables?.suggestedOffers?.filter(
            (offer) => offer.offerType === OfferType.HIGHER,
        ) ?? ([] as Offer[]);

    const isLoading =
        store.taskLoader.isLoading || store.taskVariablesLoader.isLoading;

    return (
        <BasePage
            pageName="ApplicationsCreateChooseOfferPage"
            isLoading={isLoading}
        >
            <StyledHeaderContainer>
                <div>
                    <BackButton
                        to={
                            EntityLinks.loanApplications.list?.() ??
                            '/applications'
                        }
                    >
                        {t(
                            'Page.ApplicationsCreateChooseOfferPage.Back',
                            'Back to applications',
                        )}
                    </BackButton>
                </div>
                <StyledHeaderTitle>
                    <div>
                        <Typography.Title level={1}>
                            {t(
                                'Page.ApplicationsCreateChooseOfferPage.Title',
                                'Create Application',
                            )}
                        </Typography.Title>
                        <Typography.Text type="secondary">
                            {t(
                                'Page.ApplicationsCreateChooseOfferPage.Subtitle',
                                'Step 2: Select your financing offer',
                            )}
                        </Typography.Text>
                    </div>
                </StyledHeaderTitle>
            </StyledHeaderContainer>

            {hasSuggestedOffers ? (
                <StyledAlert
                    message={t(
                        'Page.ApplicationsCreateChooseOfferPage.HasSuggestedOffers',
                        'Has suggested offers',
                    )}
                    type="success"
                />
            ) : (
                <StyledAlert
                    message={
                        <Typography.Text>
                            {t(
                                'Page.ApplicationsCreateChooseOfferPage.NoSuggestedOffers',
                                'No suggested offers',
                            )}{' '}
                            <a
                                href="mailto:support@via.marn.com"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                support@via.marn.com
                            </a>
                        </Typography.Text>
                    }
                    type="error"
                />
            )}

            <Form
                initialValues={{}}
                onFinish={handleSubmit}
                fields={fields}
                form={form}
            >
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={12}>
                        {instantMurabahaOffers &&
                            instantMurabahaOffers.map(
                                (offer: Offer, index: number) => {
                                    return (
                                        <Card key={index}>
                                            <Typography.Title level={3}>
                                                {t(
                                                    'Page.ApplicationsCreateChooseOfferPage.InstantMurabaha',
                                                    'Instant Murabaha',
                                                )}
                                            </Typography.Title>
                                            <Typography.Text type="secondary">
                                                <TextWrap>
                                                    {offer.partnerName}
                                                </TextWrap>
                                            </Typography.Text>
                                            <p>
                                                <TextWrap>
                                                    {getOfferCardCondition(
                                                        offer,
                                                    )}
                                                </TextWrap>
                                            </p>
                                            <Button
                                                type="primary"
                                                block
                                                onClick={() => {
                                                    handleChooseOffer(offer);
                                                }}
                                            >
                                                {t(
                                                    'Page.ApplicationsCreateChooseOfferPage.ChooseOfferInstantMurabaha',
                                                    'Select this offer',
                                                )}
                                            </Button>
                                        </Card>
                                    );
                                },
                            )}
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        {murabahaOffers &&
                            murabahaOffers.map(
                                (offer: Offer, index: number) => {
                                    return (
                                        <Card key={index}>
                                            <Typography.Title level={3}>
                                                {t(
                                                    'Page.ApplicationsCreateChooseOfferPage.Murabaha',
                                                    'Murabaha',
                                                )}
                                            </Typography.Title>
                                            <Typography.Text type="secondary">
                                                {t(
                                                    'Page.ApplicationsCreateChooseOfferPage.RequiresAdditionalReview',
                                                    'Requires additional review',
                                                )}
                                            </Typography.Text>
                                            <p>
                                                <TextWrap>
                                                    {getOfferCardCondition(
                                                        offer,
                                                    )}
                                                </TextWrap>
                                            </p>
                                            <Button
                                                type="primary"
                                                block
                                                onClick={() => {
                                                    handleChooseOffer(offer);
                                                }}
                                            >
                                                {t(
                                                    'Page.ApplicationsCreateChooseOfferPage.ChooseOfferMurabaha',
                                                    'Select this offer',
                                                )}
                                            </Button>
                                        </Card>
                                    );
                                },
                            )}
                    </Col>
                </Row>
                <HiddenField
                    name="chosenOffer"
                    rules={[ValidationRules.required]}
                />
                <HiddenField
                    name="cancel"
                    value={false}
                    rules={[ValidationRules.required]}
                />
            </Form>
        </BasePage>
    );
});

const StyledHeaderContainer = styled.div`
    display: flex;
    gap: 16px;
    flex-direction: column;
`;

const StyledHeaderTitle = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
    margin-bottom: 24px;

    h1 {
        flex: 1;
        margin: 0;
    }
`;

const TextWrap = styled(Typography.Text)`
    white-space: pre-wrap;
`;

const getOfferCardCondition = (offer: Offer) => {
    if (i18next.language === 'ar' && offer.offerCardArabic) {
        return offer.offerCardArabic;
    }

    return offer.offerCardEnglish;
};

const StyledAlert = styled(Alert)`
    margin-bottom: 16px;
    text-align: center;
`;
